import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { makeStyles } from "tss-react/mui";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { Box, Checkbox, Theme } from "@mui/material";

import {
  MultiApiTypeFormDetails,
  Item,
  Option,
  OptionList,
  ApiType,
} from "../interfaces";
import { isObject } from "../../services/utils";
import { GenericObject } from "../../services/Price";
import { useDebounce } from "../../hooks/debounce";
import { getSelectFilterValues } from "../utils";
import { reducePath } from "../utils";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { SX_CONTROLLED_AUTOCOMPLETE_SIZE } from "../styles";

const useStyles = makeStyles()((theme: Theme) => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    flex: "0 1 224px",
  },
}));

/**
 * Generate selector with multi api url
 *
 * This component get api url and generate options according other input value
 *
 * @param filter
 * @param field
 * @param meta
 * @param helpers
 * @param classes
 * @param row
 */
export const MultiSelect = ({
  filter,
  field,
  meta,
  helpers,
  row,
}: {
  filter: MultiApiTypeFormDetails;
  field: any;
  meta: any;
  helpers: any;
  row?: GridRowModel;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const { values } = useFormikContext<any>();
  const [options, setOptions] = useState<OptionList>([]);
  const [apiData, setApiData] = useState<ApiType | null>(null);
  const [apiUrl, setApiUrl] = useState("");
  const [initialValues, setInitialValues] = useState<string | null>(null);
  const [configParams, setConfigParams] = useState<GenericObject>(() => {
    const params: GenericObject = {};

    if (apiData && apiData.apiParams) {
      for (const [key, value] of Object.entries(apiData.apiParams)) {
        //Simple update
        if (isObject(value) && row) {
          const data = (value as any).path.split(".");
          params[key] = reducePath(row, data);
        } else if (!isObject(value)) {
          params[key] = value;
        }
      }
    }

    return params;
  });

  const debounceSearchValue = useDebounce(initialValues, 400);

  const query = useQuery(
    [apiUrl, configParams, debounceSearchValue],
    () =>
      getSelectFilterValues<Item>(apiUrl, configParams, debounceSearchValue),
    { enabled: !!apiUrl }
  );

  const autocompleteValue = useMemo(() => {
    if (!filter.multiple) {
      return options.find((opt) => opt.key === field.value) || null;
    }

    if (!Array.isArray(field.value) && field.value !== "ALL") {
      return [];
    }

    return options.filter((opt) => field.value.includes(opt.key));
  }, [filter.multiple, field.value, options]);

  // Generate api params if exist
  useEffect(() => {
    const params: GenericObject = {};

    if (apiData && apiData.apiParams) {
      for (const [key, value] of Object.entries(apiData.apiParams)) {
        //Simple update
        if (isObject(value) && row) {
          const data = (value as any).path.split(".");
          params[key] = reducePath(row, data);
        } else if (!isObject(value)) {
          params[key] = value;
        }
      }
    }

    setConfigParams(params);
  }, [apiData, row]);

  // Generate selector options
  useEffect(() => {
    if (query.data && apiData) {
      /**
       * Due to mismatch between what we display and what we need to send to backend if option is "ALL" ("Tous" and "Tout"), we add key param
       * id => is for Mui differentiation between option
       * label => what is displayed in selector
       * key => what we send to backend
       */
      const generateData = query.data.results.map((option) => ({
        id: option.id,
        label: option[apiData.labelKey],
        key: apiData.dataKey ? option[apiData.dataKey] : option.id,
      }));

      setOptions(generateData);
    } else if (apiData && apiData.defaultValue && apiData.api === "") {
      setOptions([
        {
          id: 0,
          label: intl.formatMessage({ id: apiData.defaultValue }),
          key:
            apiData.defaultValue === "ALL"
              ? "ALL"
              : intl.formatMessage({ id: apiData.defaultValue }),
        },
      ]);
    }
  }, [query.data, apiData, intl]);

  // Select api data according filter value to watch
  useEffect(() => {
    const watchFilterValue = values[filter.multiApi.watchFilter];
    const apiData = filter.multiApi.apis.filter(
      (el) => el.caseOfValueIs === watchFilterValue
    );

    if (apiData[0]) {
      setApiData(apiData[0]);
      setApiUrl(apiData[0].api);
    }
  }, [filter.multiApi.watchFilter, filter.multiApi.apis, values]);

  return (
    <Box className={basicsClasses.field}>
      <CustomAutocomplete
        disabled={filter.readonly}
        disableCloseOnSelect={filter.multiple}
        multiple={filter.multiple}
        limitTags={
          filter.multiple && filter.maxTag && !filter.controlledInputSize
            ? filter.maxTag
            : filter.multiple && filter.controlledInputSize
            ? 1
            : undefined
        }
        getOptionLabel={(option) => {
          return option.label || "";
        }}
        isOptionEqualToValue={(option: Option, value: Option | null) => {
          return option.id === value?.id;
        }}
        loading={query.isLoading}
        options={options}
        onBlur={field.onBlur}
        onChange={(e, newValue) => {
          if (Array.isArray(newValue)) {
            return helpers.setValue(newValue.map((value) => value.key));
          } else {
            return helpers.setValue(newValue?.key);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason !== "reset") {
            setInitialValues(newInputValue);
          }
        }}
        label={intl.formatMessage({ id: filter.label })}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
        renderOptions={(props, option, state) => {
          return (
            <li {...props} key={option.id}>
              {filter.multiple && (
                <Checkbox sx={{ mr: 1, p: 0 }} checked={state.selected} />
              )}
              {option.label}
            </li>
          );
        }}
        size="small"
        sx={filter.controlledInputSize ? SX_CONTROLLED_AUTOCOMPLETE_SIZE : {}}
        value={autocompleteValue}
      />
    </Box>
  );
};
